import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, Legend } from 'recharts';
import { MessageSquare, Search, X, Layers, Building2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { PERSONA_COLORS } from '../../utils/personaColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessageVolumeChart = ({ data }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewMode, setViewMode] = useState('groups'); // Default to 'groups'
  const [groupsData, setGroupsData] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Fetch groups data on mount
  useEffect(() => {
    setLoadingGroups(true);
    axios.get(`${API}/plan-groups`)
      .then(res => {
        setGroupsData(res.data);
        setLoadingGroups(false);
      })
      .catch(err => {
        console.error('Failed to fetch groups:', err);
        setLoadingGroups(false);
      });
  }, []);

  // Clear selection when switching views
  useEffect(() => {
    setSelectedClient(null);
    setSearchTerm('');
  }, [viewMode]);

  // Prepare scatter data for individual plans
  const planScatterData = useMemo(() => {
    const byPersona = {};
    
    data.forEach(client => {
      const persona = client.persona || 'Unknown';
      if (!byPersona[persona]) {
        byPersona[persona] = [];
      }
      
      byPersona[persona].push({
        x: client.messagesReceived || 0,
        y: client.revenue || 0,
        name: client.planName || client.id,
        id: client.id,
        manager: client.manager,
        messageCost: client.messageCost || 0,
        margin: client.margin || 0,
        persona,
        type: 'plan'
      });
    });
    
    return byPersona;
  }, [data]);

  // Prepare scatter data for OC groups
  const groupScatterData = useMemo(() => {
    if (!groupsData?.groups) return {};
    
    const byPersona = {};
    
    groupsData.groups.forEach(group => {
      const persona = group.dominantPersona || 'Unknown';
      if (!byPersona[persona]) {
        byPersona[persona] = [];
      }
      
      // Estimate message count from message cost (assuming $10 per message * 3 annualization)
      const estimatedMessages = Math.round((group.totalMessageCost || 0) / 30);
      
      byPersona[persona].push({
        x: estimatedMessages,
        y: group.totalRevenue || 0,
        name: group.basePlan,
        id: group.basePlan,
        manager: group.primaryManager,
        planCount: group.planCount,
        messageCost: group.totalMessageCost || 0,
        margin: group.margin / 100 || 0,
        profit: group.profit,
        persona,
        type: 'group'
      });
    });
    
    return byPersona;
  }, [groupsData]);

  const activeScatterData = viewMode === 'plans' ? planScatterData : groupScatterData;

  // Search results
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const term = searchTerm.toLowerCase();
    const results = [];
    
    Object.values(activeScatterData).flat().forEach(item => {
      const matchesName = item.name?.toLowerCase().includes(term);
      const matchesId = item.id?.toLowerCase().includes(term);
      const matchesManager = item.manager?.toLowerCase().includes(term);
      
      if (matchesName || matchesId || matchesManager) {
        results.push(item);
      }
    });
    
    return results.slice(0, 10);
  }, [searchTerm, activeScatterData]);

  // Highlighted data
  const highlightedData = useMemo(() => {
    if (!selectedClient) return [];
    return [selectedClient];
  }, [selectedClient]);

  // Calculate statistics
  const stats = useMemo(() => {
    const items = Object.values(activeScatterData).flat();
    const withMessages = items.filter(c => (c.x || 0) > 0);
    const totalMessages = withMessages.reduce((sum, c) => sum + (c.x || 0), 0);
    const totalRevenue = items.reduce((sum, c) => sum + (c.y || 0), 0);
    const avgMessages = withMessages.length > 0 ? totalMessages / withMessages.length : 0;
    const avgRevenue = items.length > 0 ? totalRevenue / items.length : 0;
    
    let correlation = 0;
    if (withMessages.length > 2) {
      const meanX = avgMessages;
      const meanY = avgRevenue;
      let numerator = 0;
      let denomX = 0;
      let denomY = 0;
      
      withMessages.forEach(c => {
        const dx = (c.x || 0) - meanX;
        const dy = (c.y || 0) - meanY;
        numerator += dx * dy;
        denomX += dx * dx;
        denomY += dy * dy;
      });
      
      const denom = Math.sqrt(denomX * denomY);
      correlation = denom > 0 ? numerator / denom : 0;
    }
    
    return { avgMessages, avgRevenue, correlation, count: items.length, withMessages: withMessages.length };
  }, [activeScatterData]);

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearchTerm('');
  };

  const clearSelection = () => {
    setSelectedClient(null);
    setSearchTerm('');
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      
      if (d.type === 'group') {
        return (
          <div className={`p-4 rounded-xl border shadow-xl min-w-[220px] ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-blue-500" />
              <p className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>Group {d.name}</p>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-500">
                {d.planCount} plans
              </span>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Manager:</span>
                <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{d.manager}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Est. Emails:</span>
                <span className="text-cyan-500">{d.x.toLocaleString()}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Total Revenue:</span>
                <span className="text-emerald-500">${d.y.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Message Cost:</span>
                <span className="text-cyan-500">${d.messageCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Persona:</span>
                <span style={{ color: PERSONA_COLORS[d.persona] }}>{d.persona}</span>
              </div>
            </div>
          </div>
        );
      }
      
      return (
        <div className={`p-4 rounded-xl border shadow-xl min-w-[200px] ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
          <p className={`font-bold text-sm mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Plan {d.name}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Manager:</span>
              <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{d.manager}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Messages:</span>
              <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{d.x.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Revenue:</span>
              <span className="text-emerald-500">${d.y.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Message Cost:</span>
              <span className="text-cyan-500">${d.messageCost.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>Persona:</span>
              <span style={{ color: PERSONA_COLORS[d.persona] }}>{d.persona}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`p-6 rounded-2xl border ${isDark ? 'bg-[#111113] border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className={`text-lg font-bold flex items-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <MessageSquare className="w-5 h-5 mr-2 text-cyan-500" />
            Email Volume vs Revenue
          </h3>
          <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {viewMode === 'plans' 
              ? 'Correlation between email volume and revenue by plan'
              : 'Correlation between email volume and revenue by OC group'}
          </p>
        </div>
        
        <div className="flex gap-3 items-center">
          {/* View Mode Toggle */}
          <div className={`flex items-center rounded-lg border p-1 ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
            <button
              onClick={() => setViewMode('plans')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                ${viewMode === 'plans' 
                  ? 'bg-cyan-500 text-white' 
                  : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <Building2 className="w-3.5 h-3.5" />
              Plans
            </button>
            <button
              onClick={() => setViewMode('groups')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors
                ${viewMode === 'groups' 
                  ? 'bg-blue-500 text-white' 
                  : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <Layers className="w-3.5 h-3.5" />
              OC Groups
            </button>
          </div>
          
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDark ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
            {stats.withMessages} with emails
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
            stats.correlation > 0.3 ? 'bg-emerald-500/10 text-emerald-500' :
            stats.correlation < -0.3 ? 'bg-red-500/10 text-red-500' :
            isDark ? 'bg-white/5 text-slate-400' : 'bg-slate-100 text-slate-600'
          }`}>
            r = {stats.correlation.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4 relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              type="text"
              placeholder={viewMode === 'plans' ? "Search by plan number or manager..." : "Search by group number or manager..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border text-sm transition-colors
                ${isDark 
                  ? 'bg-white/5 border-white/10 text-white placeholder-slate-500 focus:border-cyan-500/50' 
                  : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-cyan-500'
                } focus:outline-none focus:ring-1 focus:ring-cyan-500/20`}
            />
          </div>
          {selectedClient && (
            <button
              onClick={clearSelection}
              className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2
                ${isDark 
                  ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10' 
                  : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'}`}
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
        
        {/* Search Results Dropdown */}
        {searchResults.length > 0 && searchTerm && (
          <div className={`absolute z-10 w-full mt-1 rounded-lg border shadow-lg max-h-64 overflow-y-auto
            ${isDark ? 'bg-[#1a1a1c] border-white/10' : 'bg-white border-slate-200'}`}>
            {searchResults.map((item) => (
              <button
                key={item.id}
                onClick={() => handleSelectClient(item)}
                className={`w-full px-4 py-3 text-left text-sm transition-colors flex justify-between items-center
                  ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
              >
                <div className="flex items-center gap-2">
                  {item.type === 'group' && <Layers className="w-3.5 h-3.5 text-blue-500" />}
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.name}</span>
                  {item.type === 'group' && (
                    <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-500/20 text-blue-500">{item.planCount} plans</span>
                  )}
                  <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{item.manager}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-cyan-500 text-xs">{item.x.toLocaleString()} msgs</span>
                  <span className="text-emerald-500 text-xs">${(item.y/1000).toFixed(1)}k</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Item Info */}
      {selectedClient && (
        <div className={`mb-4 p-4 rounded-xl border ${
          viewMode === 'groups'
            ? isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'
            : isDark ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-cyan-50 border-cyan-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {selectedClient.type === 'group' && <Layers className="w-4 h-4 text-blue-500" />}
              <span className={`text-sm font-bold ${viewMode === 'groups' ? (isDark ? 'text-blue-400' : 'text-blue-700') : (isDark ? 'text-cyan-400' : 'text-cyan-700')}`}>
                {selectedClient.type === 'group' ? 'Group' : 'Plan'}: {selectedClient.name}
              </span>
              {selectedClient.type === 'group' && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-500">{selectedClient.planCount} plans</span>
              )}
              <span className={`text-xs ${viewMode === 'groups' ? (isDark ? 'text-blue-500/70' : 'text-blue-600') : (isDark ? 'text-cyan-500/70' : 'text-cyan-600')}`}>
                ({selectedClient.manager})
              </span>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="text-cyan-500">{selectedClient.x.toLocaleString()} emails</span>
              <span className="text-emerald-500">Revenue: ${selectedClient.y.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              <span className="text-cyan-500">Email Cost: ${selectedClient.messageCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {viewMode === 'groups' && loadingGroups && (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          <span className={`ml-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading groups...</span>
        </div>
      )}

      {/* Chart */}
      {(!loadingGroups || viewMode === 'plans') && (
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 40, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
              <XAxis 
                type="number" 
                dataKey="x" 
                name="Emails"
                tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(val) => val >= 1000 ? `${(val/1000).toFixed(0)}k` : val}
                label={{ value: 'Email Volume', position: 'bottom', offset: 20, fill: isDark ? '#64748b' : '#94a3b8', fontSize: 12 }}
              />
              <YAxis 
                type="number" 
                dataKey="y" 
                name="Revenue"
                tick={{ fontSize: 11, fill: isDark ? '#94a3b8' : '#64748b' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`}
                label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft', offset: -10, fill: isDark ? '#64748b' : '#94a3b8', fontSize: 12 }}
              />
              <ZAxis range={viewMode === 'groups' ? [60, 200] : [50, 150]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top" 
                height={36}
                formatter={(value) => <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>{value}</span>}
              />
              
              {Object.entries(activeScatterData).map(([persona, points]) => (
                <Scatter 
                  key={persona}
                  name={persona} 
                  data={points} 
                  fill={PERSONA_COLORS[persona]}
                  fillOpacity={selectedClient ? 0.3 : 0.7}
                />
              ))}
              
              {/* Highlighted item */}
              {highlightedData.length > 0 && (
                <Scatter 
                  name="Selected"
                  data={highlightedData}
                  fill="#ffffff"
                  stroke={viewMode === 'groups' ? '#3b82f6' : '#06b6d4'}
                  strokeWidth={3}
                  shape={(props) => {
                    const { cx, cy } = props;
                    const color = viewMode === 'groups' ? '#3b82f6' : '#06b6d4';
                    return (
                      <g>
                        <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0.3} />
                        <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2} />
                        <circle cx={cx} cy={cy} r={20} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 4">
                          <animate attributeName="r" from="15" to="25" dur="1.5s" repeatCount="indefinite" />
                          <animate attributeName="opacity" from="1" to="0" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                      </g>
                    );
                  }}
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Summary stats */}
      <div className={`mt-4 pt-4 border-t grid grid-cols-4 gap-4 ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {viewMode === 'plans' ? 'Total Plans' : 'Total Groups'}
          </p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{stats.count}</p>
        </div>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Avg Emails</p>
          <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{Math.round(stats.avgMessages).toLocaleString()}</p>
        </div>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Avg Revenue</p>
          <p className="text-lg font-bold text-emerald-500">${(stats.avgRevenue/1000).toFixed(1)}k</p>
        </div>
        <div className="text-center">
          <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Insight</p>
          <p className={`text-sm font-semibold ${
            stats.correlation > 0.3 ? 'text-emerald-500' :
            stats.correlation < -0.3 ? 'text-red-500' :
            isDark ? 'text-slate-400' : 'text-slate-600'
          }`}>
            {stats.correlation > 0.3 ? 'Positive correlation' :
             stats.correlation < -0.3 ? 'Negative correlation' :
             'No strong correlation'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MessageVolumeChart;
